import { FunctionComponent } from 'react';
import {
  connect, ConnectedComponent, MapDispatchToPropsParam, MapStateToPropsParam,
} from 'react-redux';
import qs from 'query-string';
import { Dispatch } from 'redux';

import { DEFAULT_USD_VALUES } from 'config/workflow';
import { BASIC_CURRENCIES } from 'constants/currencies';
import setDonorInfo from 'state/donor/setDonorInfo';
import { initialState as initialDonorInfoState } from 'state/donor/state';
import changeFiatAmount from 'state/pledge/changeFiatAmount';
import fetchCurrencyExchangeRateSuccess from 'state/pledge/fetchCurrencyExchangeRate.success';
import { initialState as initialPledgeState } from 'state/pledge/state';
import updatePledge from 'state/pledge/updatePledge';
import { AppReduxState } from 'state/state';
import { initialState as initialBrokerInfoState } from 'state/stock/state';
import setBrokerInfo from 'state/stock/updateBrokerInfo';
import setType from 'state/workflow/setType';
import DonationWorkflowType from 'types/workflow';
import { getUsedDesignVersion } from 'utils/componentSwitch';
import { pushDataLayerEvent } from 'utils/gtm';
import GTMEventType from 'utils/gtm/types';
import eventDataFactory from 'utils/gtm/util';
import { getInitialWorkflowType } from 'utils/organization';
import checkIsFiatDonationType from 'utils/workflow';

import DonationSuccess from './donationSuccess';
import { KEYS } from './donationSuccess.keys';

interface DefaultStateParams {
  defaultAmount: number | null;
  defaultCryptocurrency: string | null;
  coverTransactionFees?: boolean;
}

const mapStateToProps: MapStateToPropsParam<any, any, AppReduxState> = (state) => {
  const { organization } = state.organization;
  const { defaultAmount, defaultCryptocurrency } = state.donationData;
  const {
    name: organizationName,
  } = organization;
  const { brokerName } = state.stock;
  const { type: workflowType } = state.workflow;
  const { search } = window.location;
  const { display } = qs.parse(search);

  const isOtherBrokerSelected = brokerName === KEYS.OTHER_BROKER;
  const donationSentEventData = eventDataFactory(GTMEventType.DonationSent, workflowType!, { state });
  const donationShareEventData = eventDataFactory(GTMEventType.DonationShare, workflowType!, { state });

  return {
    donationSentEventData,
    donationShareEventData,
    organizationName,
    workflowType,
    organization,
    isOtherBrokerSelected,
    display,
    defaultAmount,
    defaultCryptocurrency,
  };
};

const mapDispatchToProps: MapDispatchToPropsParam<any, any> = (dispatch: Dispatch<any>) => ({
  resetFlow: (workflowType: DonationWorkflowType | null) => dispatch(setType.createAction(workflowType)),
  clearState: (workflowType: DonationWorkflowType | null, {
    coverTransactionFees,
    defaultCryptocurrency,
    defaultAmount,
  }: DefaultStateParams) => {
    dispatch(setBrokerInfo.createAction(initialBrokerInfoState()));
    dispatch(setDonorInfo.createAction(initialDonorInfoState()));
    dispatch(updatePledge.createAction(initialPledgeState(coverTransactionFees)));
    const isFiatDonationType = checkIsFiatDonationType(workflowType);
    if (isFiatDonationType) {
      dispatch(fetchCurrencyExchangeRateSuccess.createAction(1));
      dispatch(updatePledge.createAction({ currency: BASIC_CURRENCIES.USD }));
      dispatch(changeFiatAmount.createAction(DEFAULT_USD_VALUES.FIAT));
    } else {
      dispatch(updatePledge.createAction({ currency: defaultCryptocurrency ?? BASIC_CURRENCIES.BTC }));
      dispatch(changeFiatAmount.createAction(defaultAmount ?? DEFAULT_USD_VALUES.CRYPTO));
    }
  },
});

const mergeProps = (stateProps, dispatchProps) => {
  const { organization, workflowType } = stateProps;
  const version = getUsedDesignVersion();
  const donationType = getInitialWorkflowType(
    organization,
    workflowType,
    version,
  );

  const clearState = (workflowType: DonationWorkflowType | null) => (
    dispatchProps.clearState(workflowType, {
      defaultAmount: stateProps.defaultAmount,
      defaultCryptocurrency: stateProps.defaultCryptocurrency,
      coverTransactionFees: stateProps?.organization?.areFeesCoveredByDefaultEnabled,
    })
  );

  const resetFlowWithDesiredDonationType = () => {
    dispatchProps.clearState(donationType, {
      defaultAmount: stateProps.defaultAmount,
      defaultCryptocurrency: stateProps.defaultCryptocurrency,
      coverTransactionFees: stateProps?.organization?.areFeesCoveredByDefaultEnabled,
    });
    dispatchProps.resetFlow(donationType);
  };

  const onInit = () => {
    pushDataLayerEvent(stateProps.donationSentEventData);
  };

  const onShare = (platform: string) => {
    pushDataLayerEvent({
      ...stateProps.donationShareEventData,
      platform,
    });
  };

  return {
    ...stateProps,
    ...dispatchProps,
    resetFlowWithDesiredDonationType,
    onInit,
    onShare,
    clearState,
  };
};

export const DonationSuccessConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(DonationSuccess) as ConnectedComponent<FunctionComponent, {}>;

export default DonationSuccessConnect;
